import { orderBy, uniqBy, cloneDeep, clone } from "lodash";
import { ProductImageModel } from './ProductImageModel'
import { BrandModel } from '../../brand/models/BrandModel'
import { ProductModel } from './ProductModel'
import { ProductVariation, ProductBidimensionalGridModel, ProductBasicInfo } from './ProductBidimensionalGridModel'
import { CategoryModel } from '../../category/models/CategoryModel'
import { ProductVariationFormModel } from './ProductVariationFormModel'
import { ChannelModel } from '../../channel/models/ChannelModel';
import { ProductVariationPriceFormModel } from './ProductVariationPriceFormModel';
import { AllowImportEnum, ChannelEnum } from '@c2b/muven-commons';
import { ProductFormChannelInfoForm } from "./ProductFormChannelInfoModel";
import { htmlify } from "../../utils/parsers/Htmlify";


interface ChannelWithPrices {
    listPrice: number;
    sellPrice: number;
}

interface AllowImportEnumWrapper {
    enum: AllowImportEnum;
    description: string;
}

interface ImageWithUrl {
    url: string;
}

interface NewImageForm {
    url: string;
    byteLength: number;
    isMain: boolean;
    isActive: boolean,
    imageInfo: string;
    getSizeInMegabytes(): number;
}

interface ImageToBeSubmitted {
    image: string;
    sizeMb: number;
    isMain: boolean;
    isActive: boolean;
}

export class ProductFormModel {
    static getAllowImportOptions(): AllowImportEnumWrapper[] {
        return [
            {
                enum: AllowImportEnum.All,
                description: 'Importar TODAS as informações da retaguarda',
            },
            {
                enum: AllowImportEnum.AllExceptImages,
                description: 'Importar TODAS as informações da retaguarda EXCETO imagens',
            },
            {
                enum: AllowImportEnum.StockAndPrice,
                description: 'Importar apenas PREÇO e ESTOQUE da retaguarda',
            },
            {
                enum: AllowImportEnum.Stock,
                description: 'Importar apenas ESTOQUE da retaguarda',
            },
        ]
    }

    static getAllowImport(ai: AllowImportEnum): AllowImportEnumWrapper {
        return ProductFormModel.getAllowImportOptions()
            .find((a) => a.enum === ai) as AllowImportEnumWrapper;
    }

    static normalizeBeforeSubmit(pf: ProductFormModel): Partial<any> {
        const tmp = cloneDeep(pf);

        tmp.id = tmp.getId();
        tmp.allowImport = pf.allowImportWrapper.enum;
        // @ts-ignore
        tmp.allowImportWrapper = undefined;
        // @ts-ignore
        tmp._product = undefined;
        // @ts-ignore
        tmp._bidimensionalGrid = undefined;
        // @ts-ignore
        tmp.parent = undefined;
        // @ts-ignore
        tmp.children = undefined;
        // @ts-ignore
        tmp.stores = undefined;
        // @ts-ignore
        tmp.idBrand = undefined;
        // @ts-ignore
        tmp.attributes = undefined;
        // @ts-ignore
        tmp.brand?.channels = undefined
        // @ts-ignore
        tmp.categories.forEach((cat) => {
            // @ts-ignore
            cat.channels = undefined
        })

        tmp.channels = tmp.channels.map((c: any) => {
            c.pricePercentage = c.pricePercentage || null
            c.priceDecimalsPreference = parseInt(c.priceDecimalsPreference, 10)
            return c
        })

        if (tmp.hasVariations()) {
            tmp.variations.forEach((variation: any) => {
                variation.stores = undefined
            })
        }

        return tmp;
    }

    static normalizeVariationImages(variation: ProductVariation): ProductVariation {
        const v: any = cloneDeep(variation)

        v.images = (variation.images || []).map((img: any) => {
            return {
                id: img.id,
                url: img.id ? img.url : undefined,
                image: img.imageInfo,
                isMain: img.isMain,
                isActive: img.isActive,
                sizeMb: img.getSizeInMegabytes(),
            }
        })

        return v as ProductVariation
    }

    id?: string | number = undefined
    idSource?: string | number = undefined
    codeSource?: string | number = undefined
    idSourceSku?: string | number = undefined
    createdAt: Date | string | undefined = undefined
    updatedAt: Date | string | undefined = undefined
    name: string = ''
    description: string = ''
    descriptionLong: string = ''
    descriptionShort: string = ''
    videoUrl: string = ''
    eanCode: string = ''
    ncmCode: string = ''
    isbnCode: string = ''
    isActive: boolean = true
    listPrice: number = 0
    sellPrice: number = 0
    costPrice: number = 0
    showWithoutStock: boolean = true
    seoKeywords: string = ''
    seoTitle: string = ''
    seoDescription: string = ''
    skuCode: string = ''
    height: number = 0
    depth: number = 0
    width: number = 0
    weight: number = 0
    isSku: boolean = false
    isVirtual: boolean = false
    isPhysical: boolean = true
    brand?: BrandModel = undefined
    variations: ProductVariation[] = []
    variationsChannels: any[] = [];
    images: ProductImageModel[] = []
    newImages: NewImageForm[] = []
    categories: CategoryModel[] = []
    channels: ProductFormChannelInfoForm[] = []
    allowImport: AllowImportEnum = AllowImportEnum.All
    allowImportWrapper: AllowImportEnumWrapper
    private _product: ProductModel = new ProductModel()
    private _bidimensionalGrid!: ProductBidimensionalGridModel

    constructor(p?: ProductModel) {
        this._product = p ? new ProductModel(p) : new ProductModel();
        this._bidimensionalGrid = new ProductBidimensionalGridModel()

        Object.assign(this, p)

        this._normalizeAllowImportEnum()
        this._normalizePhysicalProp()
        this._normalizePrices()
        this._parseCreatedAt()
        this._parseUpdatedAt()
        this._parseBrand()
        this._parseProductImages()
        this._parseProductCategories()
        this._parseVariations()
        this._parseChannels()
        this._parseDescriptions()
    }

    setDescription(desc: string): void {
        this.description = desc;
    }

    setDescriptionShort(desc: string): void {
        this.descriptionShort = desc;
    }

    setDescriptionLong(desc: string): void {
        this.descriptionLong = desc;
    }

    hasSellPrice(): boolean {
        return !!this.sellPrice && this.sellPrice > 0;
    }

    setAllowImportWrapper(allowImportWrapper: AllowImportEnumWrapper): void {
        this.allowImportWrapper = allowImportWrapper;
    }

    isAllowImportAll(): boolean {
        return this.allowImportWrapper.enum === AllowImportEnum.All
    }

    isAllowImportAllExceptImages(): boolean {
        return this.allowImportWrapper.enum === AllowImportEnum.AllExceptImages
    }

    isAllowImportOnlyStockAndPrice(): boolean {
        return this.allowImportWrapper.enum === AllowImportEnum.StockAndPrice
    }

    isAllowImportOnlyStock(): boolean {
        return this.allowImportWrapper.enum === AllowImportEnum.Stock
    }

    updateVariationsPrices(v: ProductVariationPriceFormModel): void {
        for (const row of this.variations || []) {
            row.costPrice = v.costPrice || row.costPrice || 0
            row.listPrice = v.listPrice || row.listPrice || 0
            row.sellPrice = v.sellPrice || row.sellPrice || 0
        }
    }

    hasId(): boolean {
        return !!this.id || !!this._product?.id;
    }

    getId(): number | undefined {
        return (this._product?.firstChildId || this.id || this._product?.id) as number;
    }

    /**
     * O produto como veio da API, antes das transformações do formulário.
     * Usado pelo diagnóstico de integração, que precisa enxergar stores[] e
     * stores[].channels[] originais para explicar por que channels ficou vazio.
     */
    getSourceProduct(): ProductModel {
        return this._product;
    }

    addCategory(cat: CategoryModel): void {
        this.categories.push(cat)
    }

    setImages(images: ProductImageModel[]): void {
        this.images = this._addMorePropertiesToTheImageObject(images);
    }

    setVariationImages(skuCode: string, images: ProductImageModel[]): void {
        for (const v of this.variations) {
            if (v.skuCode === skuCode) {
                v.images = this._addMorePropertiesToTheImageObject(images);
                break;
            }
        }
    }

    resetChannelPrices(channel: ChannelWithPrices): void {
        channel.listPrice = 0;
        channel.sellPrice = 0;
    }

    hasChannels(): boolean {
        return !!this.channels?.length
    }

    setChannels(channelList: ChannelModel[]): void {

        const variations: any[] = new Array();

        if (this.hasVariations()) {
            let id = 1;
            const total = this.variations.length;
            this.variations.forEach((v) => {
                const channelName = `${v.firstName} ${v.firstVariation}, ${v.secondName} ${v.secondVariation}`;
                let uniqueStores = this._product.getUniqueStoreChannels([v]);
                // uniqueStoreChannels = uniqueStoreChannels.concat(uniqueStores);
                uniqueStores = orderBy(uniqueStores, ['name'], ['asc']);

                variations.push({
                    id: id.toString(),
                    name: channelName,
                    channels: this._parseChannelsItems(uniqueStores),
                    collapsed: total > 1
                }
                );

                id += 1;
            });
        } else {

            const missingChannels = [];

            const uniqueStoreChannels = this._product.getUniqueStoreChannels();
            // Por causa do MULTI do Mercado Livre, 
            // os canais que corresponderem ao mesmo têm que ser removidos
            // O mesmo deve ocorrer para Shopee

            this.channels = this.channels.filter(
                ch => ch.enumChannel !== ChannelEnum.MercadoLivre && ch.enumChannel !== ChannelEnum.Shopee);

            

            for (const chItem of uniqueStoreChannels) {

                let hasFoundTheChannel = false

                let channelIndex = 0;

                for (const chProduct of this.channels) {
                    if (hasFoundTheChannel) {
                        continue
                    }

                    if (chItem.channel?.id === chProduct.id) {
                        hasFoundTheChannel = true;
                    }

                    if (chProduct.id === chItem.channel?.id && !this.channels[channelIndex].idProductStoreChannel) {
                        chProduct.idProductStoreChannel = chItem.id ? chItem.id.toString() : null;

                        // Eduardo: tudo tem que ser trocado aqui!

                        this.channels[channelIndex].idProductStoreChannel = chProduct.idProductStoreChannel;
                        this.channels[channelIndex].listPrice = chItem.listPrice;
                        this.channels[channelIndex].sellPrice = chItem.sellPrice;
                        this.channels[channelIndex].pricePercentage = chItem.pricePercentage;
                        this.channels[channelIndex].priceDecimalsPreference = chItem.priceDecimalsPreference !== undefined ? chItem.priceDecimalsPreference?.toString() : null;
                        this.channels[channelIndex].availableStockPercentage = chItem.availableStockPercentage;
                        this.channels[channelIndex].stockAvailability = chItem.availableStockPercentage;
                        this.channels[channelIndex].priceUponRequest = chItem.priceUponRequest;
                    }
                    channelIndex += 1;
                }

                if (!hasFoundTheChannel) {
                    missingChannels.push(chItem)
                }

                hasFoundTheChannel = false
            }

            const channels = this._parseChannelsItems(missingChannels);
            this.channels = this.channels.concat(channels);
            this.channels = orderBy(this.channels, ['name'], ['asc']);

            variations.push({
                id: "0",
                name: "Produto Simples (Sem variação)",
                channels: this.channels,
                collapsed: false
            }
            );
        }
        this.variationsChannels = orderBy(variations, ['name'], ['asc']);
    }

    getBidimensionalGridInfo(): ProductBidimensionalGridModel {
        return this._bidimensionalGrid;
    }

    createVariationsFromProductVariationForm(p: ProductBasicInfo, v: ProductVariationFormModel): void {
        this.variations = this._bidimensionalGrid.fromProductVariationForm(p, v).getGrid();
    }

    hasSkuCode(): boolean {
        return !!this.skuCode;
    }

    setSkuCode(sku: string): void {
        this.skuCode = sku;
    }

    hasVariations(): boolean {
        return !!this.variations.length;
    }

    hasCodeSource(): boolean {
        return !!this.codeSource;
    }

    hasCategories(): boolean {
        return !!this.categories.length
    }

    hasImages(): boolean {
        return !!this.images.length || !!this.newImages.length;
    }

    addNewImageList(newImageList: NewImageForm): void {
        this.newImages.push(newImageList);
    }

    clearNewImageList(): void {
        this.newImages = [];
    }

    getSubmittableImageList(): ImageToBeSubmitted[] {
        return this.newImages.map((newImage) => {
            return {
                image: newImage.imageInfo,
                sizeMb: newImage.getSizeInMegabytes(),
                isMain: newImage.isMain,
                isActive: newImage.isActive,
            }
        })
    }

    hasMainImage(): boolean {
        return !!this.images.find((img) => {
            return img.isMain && img.isActive
        })
    }

    deleteImage(image: ImageWithUrl): void {
        for (let i = 0; i < this.images.length; i++) {
            if (this.images[i].url === image.url) {
                this.images.splice(i, 1)
                break
            }
        }

        for (let i = 0; i < this.newImages.length; i++) {
            if (this.newImages[i].url === image.url) {
                this.newImages.splice(i, 1)
                break
            }
        }
    }

    deleteVariationImage(variation: ProductVariation, image: ImageWithUrl): void {
        for (let i = 0; i < variation.images.length; i++) {
            if (variation.images[i].url === image.url) {
                variation.images.splice(i, 1)
                break
            }
        }
    }

    private _parseBrand(): void {
        this.brand = new BrandModel(this.brand)
    }

    private _parseCreatedAt(): void {
        if (typeof this.createdAt === 'string') {
            this.createdAt = new Date(this.createdAt)
        }
    }

    private _parseUpdatedAt(): void {
        if (typeof this.updatedAt === 'string') {
            this.updatedAt = new Date(this.updatedAt)
        }
    }

    private _parseProductImages(): void {
        this.images = this._addMorePropertiesToTheImageObject(this.images);
    }

    private _parseProductCategories(): void {
        const categories = this._product.categories || []
        const uniqCategories = uniqBy(categories, 'category.id');

        this.categories = uniqCategories.map((pc) => {
            return new CategoryModel(pc.category)
        })
    }

    private _normalizePrices(): void {
        this.costPrice = this.costPrice || 0;
        this.listPrice = this.listPrice || 0;
        this.sellPrice = this.sellPrice || 0;

        const hasOnlyOneChild = this._product?.children?.length === 1

        if (hasOnlyOneChild) {
            const isGrid = !!this._product?.children?.find((c) => {
                return c.hasAttributeTypeGrid()
            })

            if (isGrid) {
                this.costPrice = this._product.children[0].costPrice || 0;
                this.listPrice = this._product.children[0].listPrice || 0;
                this.sellPrice = this._product.children[0].sellPrice || 0;
            }
        }
    }

    private _parseVariations(): void {
        this._product.children = (this._product.children || []).filter((c) => {
            return c.hasAttributeTypeGrid()
        })

        this.variations = this._bidimensionalGrid.fromProduct(this._product).getGrid() || []

        this.variations.forEach((v) => {
            v.images = this._addMorePropertiesToTheImageObject(v.images);
        })
    }

    private _parseChannels(): void {
        const channels = [];
        for (const storeChannel of this._product.getStoreChannelsWithIdExternalChannel()) {
            let priceDecimalsPreference

            if (Number.isInteger(storeChannel.priceDecimalsPreference)) {
                if (storeChannel.priceDecimalsPreference as number < 10) {
                    priceDecimalsPreference = `0${storeChannel.priceDecimalsPreference}`
                } else {
                    priceDecimalsPreference = `${storeChannel.priceDecimalsPreference}`
                }
            }

            channels.push(new ProductFormChannelInfoForm({
                id: storeChannel.channel!.id as string,
                enumChannel: storeChannel.channel!.enumChannel,
                name: storeChannel.channel!.name,
                availableStockPercentage: storeChannel.availableStockPercentage,
                listPrice: storeChannel.listPrice,
                sellPrice: storeChannel.sellPrice,
                customPriceType: storeChannel.customPriceType,
                pricePercentage: storeChannel.pricePercentage,
                priceDecimalsPreference,
                priceUponRequest: storeChannel.priceUponRequest,
                hasMinimalStock: storeChannel.hasMinimalStock,
                minimalStock: storeChannel.minimalStock,
                isActive: storeChannel.isActive,
                validInChannel: storeChannel.validInChannel,
                collapsed: true,
            }))
        }

        this.channels = orderBy(channels, ['name'], ['asc'])
    }

    private _normalizePhysicalProp(): void {
        this.isPhysical = !this.isVirtual
    }

    private _normalizeAllowImportEnum(): void {
        this.allowImportWrapper = ProductFormModel.getAllowImportOptions().find((allowImport) => {
            return this.allowImport === allowImport.enum
        }) as AllowImportEnumWrapper;
    }

    private _addMorePropertiesToTheImageObject(imageList: ProductImageModel[]): ProductImageModel[] {
        return imageList.map((img) => {
            return {
                ...img,
                updatingMainAttribute: false,
                beingDeleted: false,
            }
        })
            .map((img) => {
                return new ProductImageModel(img);
            });
    }

    private _parseDescriptions(): void {
        if (/(\r\n|\n|\r)/gm.test(this.description)) {
            this.description = htmlify(this.description)
        }

        if (/(\r\n|\n|\r)/gm.test(this.descriptionShort)) {
            this.descriptionShort = htmlify(this.descriptionShort)
        }

        if (/(\r\n|\n|\r)/gm.test(this.descriptionLong)) {
            this.descriptionLong = htmlify(this.descriptionLong)
        }
    }

    private _parseChannelsItems(stores: any): ProductFormChannelInfoForm[] {
        const channels: any = [];
        stores.forEach((store: any) => {

            channels.push(new ProductFormChannelInfoForm({
                id: store.channel.id,
                idExternalChannel: store.idExternalChannel,
                idProductStoreChannel: store.id,
                enumChannel: store.channel.enumChannel,
                name: store.channel.name,
                availableStockPercentage: store.availableStockPercentage,
                stockAvailability: store.availableStockPercentage,
                listPrice: store.listPrice,
                sellPrice: store.sellPrice,
                customPriceType: store.customPriceType,
                pricePercentage: store.pricePercentage,
                priceDecimalsPreference: store.priceDecimalsPreference,
                priceUponRequest: store.priceUponRequest,
                hasMinimalStock: store.hasMinimalStock,
                minimalStock: store.minimalStock,
                isActive: store.isActive,
                validInChannel: store.validInChannel,
                collapsed: true,
            }));

        });
        return channels;
    }
}
